/**
 * NeedsAttentionPanel — tab body for the Inventory detail page's
 * "Needs Attention" tab. Lists on-hand EPCs whose reliability bucket in
 * `v2_tag_reliability_snapshot` is FLICKERY or UNRELIABLE, so operators
 * can pull specific physical tags for inspection / re-encoding.
 *
 * Loads on first mount from /api/boxes/{scu}/needs-attention. If a
 * bucket is UNRELIABLE it sorts to the top (worse than FLICKERY).
 */
import React, { useEffect, useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { fmtUtc } from "../../lib/format";
import {
  fetchNeedsAttention,
  InventoryApiError,
  type SmartboxNeedsAttention,
} from "../../services/inventoryApi";

interface NeedsAttentionPanelProps {
  scu: string;
}

const BUCKET_STYLE: Record<string, string> = {
  UNRELIABLE: "bg-error-100 text-error-800 border-error-200",
  FLICKERY:   "bg-warning-100 text-warning-800 border-warning-200",
};

export const NeedsAttentionPanel: React.FC<NeedsAttentionPanelProps> = ({ scu }) => {
  const { token } = useAuth();
  const [data, setData] = useState<SmartboxNeedsAttention | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchNeedsAttention(scu, token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof InventoryApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "unknown error",
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scu, token]);

  if (loading && !data) {
    return (
      <div className="text-sm text-secondary-500 bg-white border border-secondary-200 rounded-md p-6">
        loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
        Couldn't load reliability data: {error}
      </div>
    );
  }
  if (!data || data.count === 0) {
    return (
      <div className="bg-white border border-secondary-200 rounded-md p-6 text-sm text-secondary-600">
        No tags in this smartbox are currently flagged as flickery or
        unreliable.
      </div>
    );
  }

  return (
    <section className="bg-white border border-secondary-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-secondary-100 flex items-center justify-between">
        <div className="text-sm text-secondary-700">
          <strong className="text-secondary-900">{data.count}</strong> tag
          {data.count === 1 ? "" : "s"} currently on-hand in this box are
          reading unreliably. Pull the physical tag for inspection or
          re-encoding.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">EPC</th>
              <th className="text-left px-3 py-2">Part / SKU</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2" title="Reads over the last N scans">
                Read ratio
              </th>
              <th
                className="text-right px-3 py-2"
                title="How many recent scans in a row this tag was missing"
              >
                Missing streak
              </th>
              <th className="text-left px-3 py-2 whitespace-nowrap">Snapshot</th>
            </tr>
          </thead>
          <tbody>
            {data.tags.map((t) => {
              const ratio =
                t.read_ratio == null ? "—" : `${(t.read_ratio * 100).toFixed(0)}%`;
              return (
                <tr key={t.epc} className="border-t border-secondary-100">
                  <td className="px-3 py-2 font-mono text-xs text-secondary-800">
                    {t.epc}
                  </td>
                  <td className="px-3 py-2">
                    {t.sku ? (
                      <>
                        <div className="font-mono text-secondary-900">{t.sku}</div>
                        {t.item_name && (
                          <div className="text-xs text-secondary-500">
                            {t.item_name}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-xs italic text-secondary-500">
                        unmatched EPC
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        "inline-block px-2 py-0.5 rounded border text-xs font-medium " +
                        (BUCKET_STYLE[t.bucket] ??
                          "bg-secondary-100 text-secondary-700 border-secondary-200")
                      }
                    >
                      {t.bucket === "UNRELIABLE" ? "Unreliable" : "Flickery"}
                    </span>
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums">
                    <span>{ratio}</span>
                    <div className="text-[11px] text-secondary-500">
                      {t.read_scans}/{t.total_scans}
                    </div>
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums">
                    {t.current_miss_streak}
                  </td>
                  <td className="px-3 py-2 text-xs text-secondary-500 whitespace-nowrap">
                    {fmtUtc(t.last_updated_utc)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default NeedsAttentionPanel;
